/**
 * @file planningDay.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-14
 * @modified 2026-10-14
 * @description Two rules about a transport planning row that the boards,
 *              their paperwork and the server all share (Cam, 14 Oct 2026).
 *
 *   1. OPTION DATES. An order with option dates shows on each of them; on
 *      every day but its delivery date it is a CANDIDATE — shown so the
 *      planner can move it there, but not planned for that day. Its haulier,
 *      driver and vehicle live on the order line, not per day, so a candidate
 *      row carries them on every option day; it must count on none of those
 *      days: no vehicle load, compartment, drop number or run, no day or
 *      office summary count or tonnage, no driver paperwork, no mill email,
 *      not on Today's Deliveries. `isOptionCandidate` is the one test and
 *      `rowsForTheDay` the rows that count.
 *
 *   2. DELIVERY INSTRUCTIONS. What users type as an order's delivery
 *      instructions is the order's `notes` (the "Delivery Instructions" box
 *      on Create / Edit Customer Order and in the order window). The PO-D is
 *      given a copy when it is raised, and is never updated after — notes
 *      added or changed on the order later never reached the PO, and a stock
 *      line has no PO at all. The boards used to read the PO's copy only.
 *      A row's instructions are now the order's, with the PO's own text as
 *      well when it says something the order does not (it may have been
 *      typed on the PO — loading instructions for the mill, say).
 *      `withDeliveryInstructions` stamps that onto each row's
 *      `delivery_instructions`, keeping the PO's own text as
 *      `po_delivery_instructions` so doing it twice changes nothing (a
 *      past-day snapshot taken before this has no such field and is worked
 *      out the same way from its two texts).
 *
 *   The server's utils/planningDay.js is GENERATED from this file by
 *   scripts/sync-shared-utils.js — edit this one and re-run it.
 */

/** An order showing on one of its option dates that is not the chosen one. */
export const isOptionCandidate = (row) => !!(row && row.has_option_dates && !row.is_selected_day);

/** The rows that count on the day: every row but an option-date candidate. */
export const rowsForTheDay = (rows) => (rows || []).filter(r => !isOptionCandidate(r));

const clean = (v) => String(v == null ? '' : v).trim();

/** The PO's own instructions on a row (before this rule, the row's only ones). */
export function poInstructionsOf(row) {
  if (!row) return null;
  return Object.prototype.hasOwnProperty.call(row, 'po_delivery_instructions')
    ? row.po_delivery_instructions
    : row.delivery_instructions;
}

/**
 * A planning row's delivery instructions: the order's (`order_notes`), and
 * the PO's own text when it adds something — one when they say the same, or
 * one already says the other; both, the order's first, otherwise.
 */
export function deliveryInstructionsOf(row) {
  if (!row) return '';
  const order = clean(row.order_notes);
  const po = clean(poInstructionsOf(row));
  if (!order) return po;
  if (!po || po === order || order.includes(po)) return order;
  if (po.includes(order)) return po;
  return `${order}\n${po}`;
}

/** Each row with its delivery instructions worked out (see above). */
export const withDeliveryInstructions = (rows) => (rows || []).map(r => (r ? {
  ...r,
  po_delivery_instructions: poInstructionsOf(r) == null ? null : poInstructionsOf(r),
  delivery_instructions: deliveryInstructionsOf(r) || null
} : r));
